import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSubCollectionsAction,
  deleteSubCollectionAction,
  setSubCollectionEdit,
  clearSubCollectionEdit,
} from '../../../../redux/actions/masters/styles/subCollection/subCollectionAction';
import { fetchCollections } from '../../../../api/masters/styles/collection/collectionApi';
import { showConfirm, showInfo } from '../../../../utills/sweetAlert';
import SearchIcon from '@mui/icons-material/Search';

const cardStyle = {
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  padding: 0,
  minWidth: 0,
  marginBottom: 0,
  fontFamily: 'Poppins, sans-serif',
};
const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
};
const titleStyle = {
  fontWeight: 700,
  fontSize: 18,
  color: '#111',
  letterSpacing: 0.7,
};
const newBtnStyle = {
  background: 'linear-gradient(135deg, #00C853 0%, #43A047 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 26px',
  fontWeight: 700,
  fontSize: 16,
  cursor: 'pointer',
  marginRight: 12,
  letterSpacing: 0.5,
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 8px rgba(0, 200, 83, 0.3)',
};
const searchContainer = {
  position: 'relative',
  minWidth: 200,
  marginLeft: 8,
};
const searchInput = {
  width: 200,
  padding: '10px 36px 10px 38px',
  border: '1.5px solid #C8E6C9',
  borderRadius: 8,
  fontSize: 16,
  color: '#1B5E20',
  background: '#fff',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border 0.3s',
};
const searchIconStyle = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#00C853',
  fontSize: 20,
  pointerEvents: 'none',
};
const actionStyle = {
  cursor: 'pointer',
  marginRight: 10,
  background: 'none',
  border: 'none',
  borderRadius: 6,
  padding: 4,
  color: '#757575',
  transition: 'all 0.3s ease',
};

const SubCollectionTable = ({ onNew }) => {
  const dispatch = useDispatch();
  const { list = [], loading, error } = useSelector((state) => state.subCollection);
  const [search, setSearch] = useState('');
  const [hoveredAction, setHoveredAction] = useState({});
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    dispatch(fetchSubCollectionsAction());
    fetchCollections().then(setCollections);
  }, [dispatch]);

  const handleEdit = async (subCollection) => {
    await showInfo('Edit Sub Collection', `Editing sub collection: ${subCollection.sub_collection_name}`);
    dispatch(setSubCollectionEdit(subCollection));
  };

  const handleDelete = async (id, name) => {
    const confirmed = await showConfirm('Trash Sub Collection', `Are you sure you want to trash sub collection: ${name}?`, 'Trash', 'Cancel');
    if (confirmed) {
      dispatch(deleteSubCollectionAction(id));
    }
  };

  const handleNew = () => {
    dispatch(clearSubCollectionEdit());
    if (typeof onNew === 'function') onNew();
  };

  const getCollectionName = (collectionId) => {
    const col = collections.find(c => c.collection_id === collectionId);
    return col ? col.collection_name : '-';
  };

  const filteredSubCollections = list.filter(sub =>
    sub.sub_collection_name && sub.sub_collection_name.toLowerCase().includes(search.toLowerCase()) && sub.trash === 0
  );

  // Helper to check if error is a handled code error
  const isHandledCodeError = error && (
    error.includes('409') ||
    error.toLowerCase().includes('already exists') ||
    error.toLowerCase().includes('cannot be reused')
  );

  const columns = [
    {
      name: 'Subcollection Code',
      selector: row => row.sub_collection_code,
      sortable: true,
      width:'200px',
    },
    {
      name: 'Subcollection Name',
      selector: row => row.sub_collection_name,
      sortable: true,
      width:'200px',
    },
    {
      name: 'Collection Name',
      selector: row => row.collection_name || '-',
      sortable: true,
      width:'200px',
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '110px',
      cell: row => (
        <span style={{ color: row.status === 'Active' ? '#00C853' : '#d32f2f', fontWeight: 600 }}>{row.status}</span>
      ),
    },
    {
      name: 'Actions',
      cell: row => (
        <>
          <button
            style={actionStyle}
            onClick={() => handleEdit(row)}
            title="Edit"
            onMouseEnter={() => setHoveredAction({ id: row.sub_collection_id, type: 'edit' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <EditIcon fontSize="small" />
            {hoveredAction.id === row.sub_collection_id && hoveredAction.type === 'edit' && (
              <span style={{ marginLeft: 4, color: '#1B5E20', fontWeight: 600 }}>Edit</span>
            )}
          </button>
          <button
            style={actionStyle}
            onClick={() => handleDelete(row.sub_collection_id, row.sub_collection_name)}
            title="Trash"
            onMouseEnter={() => setHoveredAction({ id: row.sub_collection_id, type: 'delete' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <DeleteOutlineIcon fontSize="small" />
            {hoveredAction.id === row.sub_collection_id && hoveredAction.type === 'delete' && (
              <span style={{ marginLeft: 4, color: '#d32f2f', fontWeight: 600 }}>Trash</span>
            )}
          </button>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '140px',
    },
  ];

  return (
    <div style={cardStyle}>
      <style>{`
        /* Custom horizontal scrollbar for the table */
        .sub-collection-table ::-webkit-scrollbar {
          height: 6px;
          background: #f2f2f2;
        }
        .sub-collection-table ::-webkit-scrollbar-thumb {
          background: #d3d3d3;
          border-radius: 4px;
        }
        .sub-collection-table {
          scrollbar-color: #d3d3d3 #f2f2f2;
          scrollbar-width: thin;
        }
      `}</style>
      <div style={headerStyle}>
        <span style={titleStyle}>Sub Collections</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button style={newBtnStyle} onClick={handleNew}>+ New</button>
          <div style={searchContainer}>
            <input
              style={searchInput}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <SearchIcon style={{ ...searchIconStyle, color: '#00C853' }} />
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={filteredSubCollections}
        pagination
        highlightOnHover
        striped
        fixedHeader
        fixedHeaderScrollHeight="420px"
        progressPending={loading}
        noDataComponent={<div style={{ color: '#888', fontSize: 16 }}>No Sub Collections found.</div>}
        customStyles={{
          headRow: { style: { background: '#E8F5E9', fontWeight: 700 } },
          rows: { style: { minHeight: 48 } },
        }}
      />
      {error && !isHandledCodeError && <div style={{ color: 'red', fontSize: 15, marginTop: 8 }}>{error}</div>}
    </div>
  );
};

export default SubCollectionTable; 